/**
 * Reveal — fades content in (with a small rise) as it scrolls into view, and fades it back out once it has
 * fully left the viewport, so scrolling back up replays it. Subtle by design: 600ms, 16px, ease-out.
 *
 * - Respects prefers-reduced-motion (content simply shows, no movement).
 * - Content is always rendered (only opacity/transform change), so it stays readable to search engines
 *   and screen readers, and shows immediately if IntersectionObserver is unavailable.
 */

import React, { useEffect, useRef, useState } from 'react';

interface RevealProps {
  children: React.ReactNode;
  /** Stagger siblings, in ms. */
  delay?: number;
  className?: string;
  as?: 'div' | 'section' | 'li' | 'article';
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export const Reveal: React.FC<RevealProps> = ({ children, delay = 0, className = '', as = 'div' }) => {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [animate] = useState(() => !prefersReducedMotion() && typeof IntersectionObserver !== 'undefined');

  useEffect(() => {
    if (!animate) { setVisible(true); return; }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
        else if (entry.intersectionRatio === 0) setVisible(false);   // fully out -> fade back out
      },
      { threshold: [0, 0.12], rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [animate]);

  const Tag = as as any;
  return (
    <Tag
      ref={ref}
      className={`${animate ? 'transition-[opacity,transform] duration-700 ease-out motion-reduce:transition-none' : ''} ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      } ${className}`}
      style={animate && delay ? { transitionDelay: visible ? `${delay}ms` : '0ms' } : undefined}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
